import { Component, type OnInit } from '@angular/core';
import {
  FormControl,
  FormGroup,
  type ValidationErrors,
  Validators,
} from '@angular/forms';
import {
  BaseMatcherEditorComponent,
  type MatcherComponentRegistryService,
} from '@cdw-ae/filter-bar-core';
import type { CustomValidator } from '@cdw-ae/filter-bar-interfaces';
import { combineLatest } from 'rxjs';
import { map, startWith } from 'rxjs/operators';

@Component({
  template: `
    <div [formGroup]="state" class="text-matcher-form">
      <!-- Show operator dropdown only if matcher supports operators -->
      <mat-form-field *ngIf="supportsOperators" appearance="fill" class="operator-field">
        <mat-label>Operator</mat-label>
        <mat-select formControlName="operatorControl">
          <mat-option value="=">Equals</mat-option>
          <mat-option value=">">Greater Than</mat-option>
          <mat-option value="<">Less Than</mat-option>
          <mat-option value=">=">Greater Than or Equal</mat-option>
          <mat-option value="<=">Less Than or Equal</mat-option>
          <mat-option value="startsWith">Starts With</mat-option>
          <mat-option value="contains">Contains</mat-option>
        </mat-select>
      </mat-form-field>

      <mat-form-field appearance="fill" class="value-field">
        <mat-label>Value</mat-label>
        <input formControlName="textControl" matInput />
        <mat-error class="matcher-error" *ngFor="let error of errorMessages">
          {{ error }}
        </mat-error>
      </mat-form-field>
    </div>
  `,
  styleUrls: ['./text.matcher.scss'],
  standalone: false,
})
export class TextMatcherComponent
  extends BaseMatcherEditorComponent
  implements OnInit
{
  public override state = new FormGroup({
    textControl: new FormControl<string>(''),
    operatorControl: new FormControl<string>(''),
  });

  protected hasCustomValidators = false;
  protected errorMessages: string[] = [];
  protected supportsOperators = false;

  constructor() {
    super();
  }

  public ngOnInit(): void {
    // Check if this matcher supports operators
    this.supportsOperators =
      this.matcherOptions.selectedMatcher?.name === 'enhancedText';

    // Set up validators
    const customValidators =
      this.matcherOptions.selectedMatcher?.validators?.map(
        (validatorConfig: CustomValidator) => validatorConfig.validator
      );

    this.state.controls.textControl.setValidators(
      customValidators ?? Validators.required
    );

    // Handle initial value - could be string or object with operator
    const matchValue = this.matcherOptions.matchValue;
    let initialOperator = '=';
    let initialValue = '';

    if (
      this.supportsOperators &&
      matchValue &&
      typeof matchValue === 'object' &&
      'operator' in matchValue &&
      'value' in matchValue
    ) {
      // It's in the format { operator: string; value: string }
      const typedMatchValue = matchValue as { operator: string; value: string };
      initialOperator = typedMatchValue.operator;
      initialValue = typedMatchValue.value ?? '';
    } else if (matchValue !== null && matchValue !== undefined) {
      // It's a simple string value
      initialValue = matchValue.toString();
      // For non-operator matchers, determine default operator from matcher name
      if (this.matcherOptions.selectedMatcher?.name === 'startsWith') {
        initialOperator = 'startsWith';
      } else if (this.matcherOptions.selectedMatcher?.name === 'contains') {
        initialOperator = 'contains';
      }
    }

    this.state.controls.textControl.setValue(initialValue);

    if (this.supportsOperators) {
      this.state.controls.operatorControl.setValue(initialOperator);

      // Update valueToMatch when either operator or value changes (for operator-supporting matchers)
      combineLatest([
        this.state.controls.operatorControl.valueChanges.pipe(
          startWith(initialOperator)
        ),
        this.state.controls.textControl.valueChanges.pipe(
          startWith(initialValue)
        ),
      ]).subscribe(([operator, textValue]) => {
        this.checkErrors(
          this.state.controls.textControl.errors,
          this.matcherOptions.selectedMatcher?.validators ?? []
        );
        if (textValue !== null && textValue !== undefined) {
          this.valueToMatch = {
            operator: operator || '=',
            value: textValue,
          };
        } else {
          this.valueToMatch = null;
        }
      });
    } else {
      // For simple text matchers, just watch the text control
      this.state.controls.textControl.valueChanges.subscribe(
        (value: string | null) => {
          this.checkErrors(
            this.state.controls.textControl.errors,
            this.matcherOptions.selectedMatcher?.validators ?? []
          );
          this.valueToMatch = value;
        }
      );
    }

    if (this.matcherOptions.matchValue) {
      this.state.controls.textControl.markAsTouched();
      this.state.controls.textControl.updateValueAndValidity();
    }

    this.hasErrors$ = this.state.statusChanges.pipe(
      map((): boolean => this.state.invalid),
      startWith(this.state.invalid)
    );
  }

  public checkErrors(
    errorKeys: ValidationErrors | null,
    errorConfig: CustomValidator[]
  ): void {
    this.errorMessages = errorKeys
      ? Object.keys(errorKeys).map((key: string) => {
          const error = errorConfig.find(
            (errorConfigItem: CustomValidator) =>
              errorConfigItem.errorKey === key
          );
          return error?.errorMessage ?? 'Value is required';
        })
      : [];
  }

  /**
   * Register this component with the matcher registry
   */
  public static registerText(registry: MatcherComponentRegistryService): void {
    registry.registerComponent('enhancedText', TextMatcherComponent);
  }
}
